import { Box, Button, chakra, useColorModeValue } from '@chakra-ui/react';
import React from 'react';

import type { FormattedData } from './types';

import { space } from 'lib/html-entities';
import { useI18n } from 'lib/i18n/context';
import * as mixpanel from 'lib/mixpanel/index';
import Skeleton from 'ui/shared/chakra/Skeleton';
import IconSvg from 'ui/shared/IconSvg';

import { ADDRESS_PAGE_KEYS, ADDRESS_TOKEN_KEYS } from '../i18n';
import { getTokensTotalInfo } from '../utils/tokenUtils';

interface Props {
  isOpen: boolean;
  isLoading: boolean;
  onClick: () => void;
  data: FormattedData;
}

const TokenSelectButton = ({ isOpen, isLoading, onClick, data }: Props, ref: React.ForwardedRef<HTMLButtonElement>) => {
  const { t } = useI18n();
  const { usd, num, isOverflow } = getTokensTotalInfo(data);
  const skeletonBgColor = useColorModeValue('white', 'black');
  const btnBg = useColorModeValue('#FFFFFF', '#171717');
  const btnBorder = useColorModeValue('#E9ECEF', '#222222');
  const primaryText = useColorModeValue('#000000', '#FFFFFF');
  const secondaryText = useColorModeValue('#616161', '#999999');

  const prefix = isOverflow ? '>' : '';

  const handleClick = React.useCallback(() => {
    if (isLoading && !isOpen) {
      return;
    }

    mixpanel.logEvent(mixpanel.EventTypes.PAGE_WIDGET, { Type: 'Tokens dropdown' });
    onClick();
  }, [ isLoading, isOpen, onClick ]);

  return (
    <Box position="relative" width="100%">
      <Button
        ref={ ref }
        h="40px"
        w="100%"
        px="8px"
        pl="16px"
        pr="12px"
        variant="unstyled"
        display="flex"
        alignItems="center"
        justifyContent="flex-start"
        bg={ btnBg }
        border="1px solid"
        borderColor={ btnBorder }
        borderRadius="8px"
        onClick={ handleClick }
        isActive={ isOpen }
        aria-label={ t(ADDRESS_PAGE_KEYS.aria.tokenSelect) }
        _hover={{ borderColor: btnBorder }}
        _active={{ borderColor: btnBorder }}
      >
        <chakra.span
          fontFamily="'Oxanium', var(--chakra-fonts-body)"
          fontWeight={ 500 }
          fontSize="16px"
          color={ primaryText }
          mr="4px"
        >
          { prefix }${ usd.toFormat(2) }
        </chakra.span>
        <chakra.span
          fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
          fontWeight={ 500 }
          fontSize="12px"
          color={ secondaryText }
          whiteSpace="pre"
          maxW={{ base: 'calc(100vw - 230px)', lg: '500px' }}
          overflow="hidden"
          textOverflow="ellipsis"
        >
          { space }({ prefix }{ t(num === 1 ? ADDRESS_TOKEN_KEYS.count.tokenOne : ADDRESS_TOKEN_KEYS.count.tokenOther, { count: num }) })
        </chakra.span>
        <IconSvg
          name="arrows/east-mini"
          transform={ isOpen ? 'rotate(-90deg)' : 'rotate(90deg)' }
          transitionDuration="faster"
          boxSize={ 3 }
          ml="auto"
          flexShrink={ 0 }
          color={ secondaryText }
        />
      </Button>
      { isLoading && !isOpen && <Skeleton h="100%" w="100%" position="absolute" top={ 0 } left={ 0 } bgColor={ skeletonBgColor } borderRadius="base"/> }
    </Box>
  );
};

export default React.forwardRef(TokenSelectButton);
